import {
    cents,
    dateDay,
    defineFilters,
    enumOne,
    enumOrAny,
    flag,
    multiEnum,
    number,
    objectId,
    ratio,
    stringList,
    text,
    triState,
    type AdvancedGroup,
    type ApiValue,
    type FilterOption
} from '@/components/kit/filters'
import type {
    CardDuplicateMissingFieldsFilter,
    CardDuplicatePairStatus,
    CardDuplicateReason,
    CardDuplicateRecommendedActionFilter,
    CardDuplicateSortField
} from '@/types/card-duplicate'

import { CONDITION_OPTIONS, GAME_OPTIONS, LANGUAGE_OPTIONS, PRINTING_OPTIONS } from './card-constants'

export const DUPLICATE_STATUS_OPTIONS: FilterOption<CardDuplicatePairStatus>[] = [
    { value: 'pending', label: 'Pending', hint: 'Nobody has ruled on this pair yet' },
    { value: 'different', label: 'Different', hint: 'Confirmed as two distinct cards' },
    { value: 'merged', label: 'Merged', hint: 'One record absorbed the other' },
    { value: 'stale', label: 'Stale', hint: 'A later scan stopped seeing the pair' }
]

export const DUPLICATE_REASON_OPTIONS: FilterOption<CardDuplicateReason>[] = [
    { value: 'same_tcgplayer_id', label: 'Same TCGplayer ID' },
    { value: 'same_set_and_number', label: 'Same set and number' },
    { value: 'similar_name', label: 'Similar name' },
    { value: 'missing_identity_fields', label: 'Missing identity fields' }
]

const RECOMMENDED_ACTION_OPTIONS: FilterOption<Exclude<CardDuplicateRecommendedActionFilter, ''>>[] = [
    { value: 'merge_a_into_b', label: 'Keep B' },
    { value: 'merge_b_into_a', label: 'Keep A' },
    { value: 'different', label: 'Leave separate' },
    { value: 'none', label: 'No recommendation' }
]

const REASON_MODE_OPTIONS: FilterOption<'any' | 'all'>[] = [
    { value: 'any', label: 'Any reason' },
    { value: 'all', label: 'All reasons' }
]

const MISSING_FIELDS_OPTIONS: FilterOption<Exclude<CardDuplicateMissingFieldsFilter, ''>>[] = [
    { value: 'any', label: 'Either side incomplete' },
    { value: 'none', label: 'Both sides complete' },
    { value: 'a', label: 'A incomplete' },
    { value: 'b', label: 'B incomplete' },
    { value: 'both', label: 'Both incomplete' }
]

const SORT_OPTIONS: FilterOption<Exclude<CardDuplicateSortField, ''>>[] = [
    { value: 'score', label: 'Match score' },
    { value: 'lastSeenAt', label: 'Last seen' },
    { value: 'createdAt', label: 'First detected' },
    { value: 'updatedAt', label: 'Updated' },
    { value: 'nameSimilarity', label: 'Name similarity' },
    { value: 'completenessA', label: 'Completeness A' },
    { value: 'completenessB', label: 'Completeness B' },
    { value: 'sourceHealthA', label: 'Source health A' },
    { value: 'sourceHealthB', label: 'Source health B' }
]

const ORDER_OPTIONS: FilterOption<'asc' | 'desc'>[] = [
    { value: 'desc', label: 'Descending' },
    { value: 'asc', label: 'Ascending' }
]

export { ORDER_OPTIONS as DUPLICATE_ORDER_OPTIONS }

/**
 * The default view is the pending queue, not everything: opening the page onto four
 * thousand already-settled pairs is not a work queue. `isEmpty` follows the default
 * rather than emptiness, so the pending state draws no chip and Clear all returns
 * here instead of to "every status".
 */
const statusField = (() => {
    const base = multiEnum(DUPLICATE_STATUS_OPTIONS, { label: 'Status', apiKey: 'status', encode: 'comma' })
    const isDefault = (value: CardDuplicatePairStatus[]) => value.length === 1 && value[0] === 'pending'
    return { ...base, default: ['pending'] as CardDuplicatePairStatus[], isEmpty: isDefault }
})()

/**
 * The duplicate queue's filters, all sixty of them.
 *
 * The legacy `cardDuplicateFilters.ts` spent a thousand lines writing this set out
 * three times -- once to read the URL, once to write it, once to build the query --
 * plus a hand-rolled validator. Every one of those falls out of the declarations
 * below, and the per-field `catch` in the schemas replaces the validator: a filter
 * the operator has half-typed degrades to "not filtering" instead of blocking the
 * fetch and leaving the queue frozen with an error underneath it.
 */
export const duplicateFilters = defineFilters(
    {
        // Identity
        pairKey: text({ label: 'Pair key', max: 64 }),
        search: text({ label: 'Search' }),
        cardId: objectId({ label: 'Card ID' }),
        cardSourceId: text({ label: 'TCGplayer product ID' }),
        justTcgId: text({ label: 'JustTCG ID' }),

        // Pair
        game: enumOrAny(GAME_OPTIONS, { label: 'Game' }),
        statuses: statusField,
        recommendedAction: enumOrAny(RECOMMENDED_ACTION_OPTIONS, { label: 'Recommendation' }),
        reasons: multiEnum(DUPLICATE_REASON_OPTIONS, { label: 'Signals', encode: 'comma' }),
        reasonMode: enumOne(REASON_MODE_OPTIONS, 'any', { label: 'Signal match' }),
        missingFields: enumOrAny(MISSING_FIELDS_OPTIONS, { label: 'Missing fields' }),

        scoreMin: ratio({ label: 'Min score', apiKey: 'minScore' }),
        scoreMax: ratio({ label: 'Max score', apiKey: 'maxScore' }),
        nameSimilarityMin: ratio({ label: 'Min name similarity', apiKey: 'minNameSimilarity' }),
        nameSimilarityMax: ratio({ label: 'Max name similarity', apiKey: 'maxNameSimilarity' }),

        sameJustTcgId: triState({ label: 'Same JustTCG ID' }),
        sameSetCode: triState({ label: 'Same set code' }),
        sameNumber: triState({ label: 'Same number' }),
        merged: triState({ label: 'Merged' }),

        completenessAMin: ratio({ label: 'Min completeness A', apiKey: 'minCompletenessA' }),
        completenessAMax: ratio({ label: 'Max completeness A', apiKey: 'maxCompletenessA' }),
        completenessBMin: ratio({ label: 'Min completeness B', apiKey: 'minCompletenessB' }),
        completenessBMax: ratio({ label: 'Max completeness B', apiKey: 'maxCompletenessB' }),
        sourceHealthAMin: ratio({ label: 'Min source health A', apiKey: 'minSourceHealthA' }),
        sourceHealthAMax: ratio({ label: 'Max source health A', apiKey: 'maxSourceHealthA' }),
        sourceHealthBMin: ratio({ label: 'Min source health B', apiKey: 'minSourceHealthB' }),
        sourceHealthBMax: ratio({ label: 'Max source health B', apiKey: 'maxSourceHealthB' }),

        // Pair dates
        createdFrom: dateDay({ label: 'Detected from', boundary: 'start' }),
        createdTo: dateDay({ label: 'Detected to', boundary: 'end' }),
        updatedFrom: dateDay({ label: 'Updated from', boundary: 'start' }),
        updatedTo: dateDay({ label: 'Updated to', boundary: 'end' }),
        lastSeenFrom: dateDay({ label: 'Last seen from', boundary: 'start' }),
        lastSeenTo: dateDay({ label: 'Last seen to', boundary: 'end' }),
        reviewedFrom: dateDay({ label: 'Reviewed from', boundary: 'start' }),
        reviewedTo: dateDay({ label: 'Reviewed to', boundary: 'end' }),

        // Card metadata
        cardName: text({ label: 'Card name' }),
        set: text({ label: 'Set' }),
        setCode: text({ label: 'Set code' }),
        number: text({ label: 'Collector number' }),
        tcgplayerSkuId: text({ label: 'TCGplayer SKU' }),
        rarities: stringList({ label: 'Rarities', encode: 'comma' }),
        languages: multiEnum(LANGUAGE_OPTIONS, { label: 'Languages', encode: 'comma' }),
        conditions: multiEnum(CONDITION_OPTIONS, { label: 'Conditions', encode: 'comma' }),
        printings: multiEnum(PRINTING_OPTIONS, { label: 'Printings', encode: 'comma' }),
        hidden: triState({ label: 'Hidden' }),
        customPrice: triState({ label: 'Custom price' }),
        priceMin: cents({ label: 'Min price', apiKey: 'minPrice' }),
        priceMax: cents({ label: 'Max price', apiKey: 'maxPrice' }),
        missedSyncsMin: number({ label: 'Min missed syncs', apiKey: 'minMissedSyncs', min: 0 }),
        missedSyncsMax: number({ label: 'Max missed syncs', apiKey: 'maxMissedSyncs', min: 0 }),
        cardUpdatedFrom: dateDay({ label: 'Card updated from', boundary: 'start' }),
        cardUpdatedTo: dateDay({ label: 'Card updated to', boundary: 'end' }),

        // Live references. These decide how expensive a merge is, so they are the
        // filters that separate "safe to resolve" from "needs care".
        inventoryMin: number({ label: 'Min inventory', apiKey: 'minInventory', min: 0 }),
        inventoryMax: number({ label: 'Max inventory', apiKey: 'maxInventory', min: 0 }),
        pendingWithdrawalsMin: number({ label: 'Min pending withdrawals', apiKey: 'minPendingWithdrawals', min: 0 }),
        pendingWithdrawalsMax: number({ label: 'Max pending withdrawals', apiKey: 'maxPendingWithdrawals', min: 0 }),
        operationalWithdrawalsMin: number({
            label: 'Min operational withdrawals',
            apiKey: 'minOperationalWithdrawals',
            min: 0
        }),
        operationalWithdrawalsMax: number({
            label: 'Max operational withdrawals',
            apiKey: 'maxOperationalWithdrawals',
            min: 0
        }),
        warehouseAvailableMin: number({ label: 'Min warehouse available', apiKey: 'minWarehouseAvailable', min: 0 }),
        warehouseAvailableMax: number({ label: 'Max warehouse available', apiKey: 'maxWarehouseAvailable', min: 0 }),
        warehouseReservedMin: number({ label: 'Min warehouse reserved', apiKey: 'minWarehouseReserved', min: 0 }),
        warehouseReservedMax: number({ label: 'Max warehouse reserved', apiKey: 'maxWarehouseReserved', min: 0 }),
        buyOrdersMin: number({ label: 'Min buy orders', apiKey: 'minBuyOrders', min: 0 }),
        buyOrdersMax: number({ label: 'Max buy orders', apiKey: 'maxBuyOrders', min: 0 }),
        customPackSetsMin: number({ label: 'Min custom pack sets', apiKey: 'minCustomPackSets', min: 0 }),
        customPackSetsMax: number({ label: 'Max custom pack sets', apiKey: 'maxCustomPackSets', min: 0 }),
        packsMin: number({ label: 'Min packs', apiKey: 'minPacks', min: 0 }),
        packsMax: number({ label: 'Max packs', apiKey: 'maxPacks', min: 0 }),
        casesMin: number({ label: 'Min cases', apiKey: 'minCases', min: 0 }),
        casesMax: number({ label: 'Max cases', apiKey: 'maxCases', min: 0 }),
        hasAnyReferences: triState({ label: 'Referenced anywhere' }),
        pendingWithdrawalsOnly: flag({ label: 'Blocking a withdrawal' }),

        sortBy: enumOrAny(SORT_OPTIONS, { label: 'Sort' }),
        sortOrder: enumOne(ORDER_OPTIONS, 'desc', { label: 'Order' })
    },
    { defaultLimit: 20, maxLimit: 100 }
)

export type DuplicateSearch = ReturnType<typeof duplicateFilters.parse>

/**
 * `status` is always sent, and `all` is how the endpoint spells "no filter" -- an
 * omitted parameter means the backend applies its own default instead.
 */
export function duplicateApiParams(search: DuplicateSearch): Record<string, ApiValue> {
    const params = duplicateFilters.toApiParams(search)
    params.status = search.statuses.length === 0 ? 'all' : search.statuses.join(',')

    // `reasonMode` only means something alongside the reasons it modifies, and the
    // backend rejects a lone `all`.
    if (search.reasons.length === 0 || search.reasonMode === 'any') delete params.reasonMode

    // A direction with nothing to order by is rejected outright ("sortOrder requires
    // sortBy"), and the default sort is the "Any" state, so the direction has to drop
    // out with it rather than ride along as a stray `desc`.
    if (search.sortBy === '') delete params.sortOrder

    return params
}

/** Groups for `<AdvancedFilters>`, in the order the legacy panel used. */
export const DUPLICATE_FILTER_GROUPS: AdvancedGroup<DuplicateSearch>[] = [
    {
        title: 'Pair',
        description: 'The scan\u2019s own verdict on how alike the two records are.',
        entries: [
            'recommendedAction',
            'reasons',
            'reasonMode',
            'missingFields',
            { range: ['scoreMin', 'scoreMax'], label: 'Match score' },
            { range: ['nameSimilarityMin', 'nameSimilarityMax'], label: 'Name similarity' },
            'sameJustTcgId',
            'sameSetCode',
            'sameNumber',
            'merged'
        ]
    },
    {
        title: 'Record quality',
        description: 'Completeness is how much identity a record carries; source health is how well it syncs.',
        entries: [
            { range: ['completenessAMin', 'completenessAMax'], label: 'Completeness A' },
            { range: ['completenessBMin', 'completenessBMax'], label: 'Completeness B' },
            { range: ['sourceHealthAMin', 'sourceHealthAMax'], label: 'Source health A' },
            { range: ['sourceHealthBMin', 'sourceHealthBMax'], label: 'Source health B' },
            { range: ['missedSyncsMin', 'missedSyncsMax'], label: 'Missed syncs' }
        ]
    },
    {
        title: 'Identity',
        entries: ['pairKey', 'cardId', 'cardSourceId', 'justTcgId', 'tcgplayerSkuId']
    },
    {
        title: 'Card metadata',
        entries: [
            'cardName',
            'set',
            'setCode',
            'number',
            'rarities',
            'languages',
            'conditions',
            'printings',
            'hidden',
            'customPrice',
            { range: ['priceMin', 'priceMax'], label: 'Price' }
        ]
    },
    {
        title: 'Live references',
        description: 'What a merge would have to move. High counts are the pairs worth reading twice.',
        entries: [
            'pendingWithdrawalsOnly',
            'hasAnyReferences',
            { range: ['inventoryMin', 'inventoryMax'], label: 'Inventory' },
            { range: ['pendingWithdrawalsMin', 'pendingWithdrawalsMax'], label: 'Pending withdrawals' },
            { range: ['operationalWithdrawalsMin', 'operationalWithdrawalsMax'], label: 'Operational withdrawals' },
            { range: ['warehouseAvailableMin', 'warehouseAvailableMax'], label: 'Warehouse available' },
            { range: ['warehouseReservedMin', 'warehouseReservedMax'], label: 'Warehouse reserved' },
            { range: ['buyOrdersMin', 'buyOrdersMax'], label: 'Buy orders' },
            { range: ['customPackSetsMin', 'customPackSetsMax'], label: 'Custom pack sets' },
            { range: ['packsMin', 'packsMax'], label: 'Packs' },
            { range: ['casesMin', 'casesMax'], label: 'Cases' }
        ]
    },
    {
        title: 'Dates',
        entries: [
            'createdFrom',
            'createdTo',
            'lastSeenFrom',
            'lastSeenTo',
            'updatedFrom',
            'updatedTo',
            'reviewedFrom',
            'reviewedTo',
            'cardUpdatedFrom',
            'cardUpdatedTo'
        ]
    }
]

export const DUPLICATE_QUICK_KEYS: (keyof DuplicateSearch)[] = ['search', 'game', 'statuses', 'sortBy', 'sortOrder']

export { SORT_OPTIONS as DUPLICATE_SORT_OPTIONS }
